import { FC, FormEvent, useState } from 'react';

import { EmailOtpChallenge } from '../../../../services/emailOtpAuth';
import LoginCodeStep from './LoginCodeStep';
import LoginEmailStep from './LoginEmailStep';
import LoginPasswordStep from './LoginPasswordStep';

export const AUTH_ERROR_MSG = {
  NO_USER: "We don't have an account associated with this email, please try again.",
  INCORRECT: 'Incorrect username or password.',
};

interface LoginFormErrors {
  email: string;
  password: string;
  root: string;
}

interface LoginFormProps {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  promptForPassword: boolean;
  setPromptForPassword: (value: boolean) => void;
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  errors: LoginFormErrors;
  setErrors: (errors: LoginFormErrors) => void;
  loading: boolean;
  submitLabel: string;
  requestSignInCode: () => Promise<EmailOtpChallenge>;
  verifySignInCode: (challenge: EmailOtpChallenge, code: string) => Promise<void>;
}

const LoginForm: FC<LoginFormProps> = ({
  onSubmit,
  promptForPassword,
  setPromptForPassword,
  email,
  setEmail,
  password,
  setPassword,
  errors,
  setErrors,
  loading,
  submitLabel,
  requestSignInCode,
  verifySignInCode,
}) => {
  const [signInMethod, setSignInMethod] = useState<'password' | 'code'>('password');
  const [otpChallenge, setOtpChallenge] = useState<EmailOtpChallenge | null>(null);
  const [codeRequestLoading, setCodeRequestLoading] = useState(false);
  const emailError = errors.email || (errors.root === AUTH_ERROR_MSG.NO_USER && errors.root);
  const passwordError = errors.password || (errors.root === AUTH_ERROR_MSG.INCORRECT && errors.root);

  const onChangeEmail = () => {
    setEmail('');
    setErrors({ ...errors, email: '' });
    setPromptForPassword(false);
    setSignInMethod('password');
  };

  const onSignInWithCode = async () => {
    setCodeRequestLoading(true);
    try {
      const challenge = await requestSignInCode();
      setOtpChallenge(challenge);
      setSignInMethod('code');
    } catch (requestError) {
      if (requestError.type === 'PasswordResetRequiredException') {
        setErrors({ ...errors, password: 'Password reset required. Please reset your password.' });
        return;
      }
      setErrors({ ...errors, root:  requestError.message});
    } finally {
      setCodeRequestLoading(false);
    }
  };

  if (promptForPassword && signInMethod === 'code' && otpChallenge) {
    return (
      <LoginCodeStep
        challenge={otpChallenge}
        onChallengeChange={setOtpChallenge}
        onVerify={verifySignInCode}
        onResend={requestSignInCode}
        onChangeEmail={onChangeEmail}
        onSignInWithPassword={() => setSignInMethod('password')}
      />
    );
  }

  if (promptForPassword) {
    return (
      <LoginPasswordStep
        email={email}
        password={password}
        error={passwordError}
        loading={loading}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onPasswordChange={(value) => {
          setErrors({ ...errors, password: '' });
          setPassword(value);
        }}
        onChangeEmail={onChangeEmail}
        onSignInWithCode={onSignInWithCode}
        signInWithCodeLoading={codeRequestLoading}
      />
    );
  }

  return (
    <LoginEmailStep
      email={email}
      error={emailError}
      loading={loading}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
      onEmailChange={(value) => {
        setErrors({ ...errors, email: '' });
        setEmail(value);
      }}
    />
  );
};

export default LoginForm;
